import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import toast from 'react-hot-toast';
import { XMarkIcon, UserIcon } from '@heroicons/react/24/outline';
import AccountTypeahead, { AccountTypeaheadOption } from './account/AccountTypeahead';
import EntityDrawer from './ui/EntityDrawer';

interface CustomerData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  dateOfBirth: string;
  licenseNumber: string;
  licenseExpiry: string;
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
  companyName: string;
  companyContact: string;
  companyContactEmail: string;
  companyContactPhone: string;
  scswCustomerId: string;
  customerType: string;
  wedding: boolean;
  customerWebsite: string;
  customerStatus: string;
}

interface CreateStripeCustomerData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  companyName: string;
  accountRelationship?: {
    mode: 'none' | 'link_existing' | 'create_new';
    accountId?: string | null;
    accountName?: string | null;
    makePrimaryBillingContact?: boolean;
  };
}

interface CustomerCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CustomerEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: any;
}

export function CustomerCreateModal({ isOpen, onClose }: CustomerCreateModalProps) {
  const [accountMode, setAccountMode] = useState<'none' | 'link_existing' | 'create_new'>('none');
  const [selectedAccount, setSelectedAccount] = useState<AccountTypeaheadOption | null>(null);
  const [newAccountName, setNewAccountName] = useState('');
  const [makePrimaryBillingContact, setMakePrimaryBillingContact] = useState(false);
  const [customerData, setCustomerData] = useState<CreateStripeCustomerData>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    companyName: '',
  });
  const queryClient = useQueryClient();

  const createCustomerMutation = useMutation(
    (data: CreateStripeCustomerData) => 
      adminAPI.createCustomer?.(data) || Promise.resolve({ data: { success: true, customerId: 'new-customer' } }),
    {
      onSuccess: () => {
        toast.success('Stripe customer created successfully');
        queryClient.invalidateQueries('admin-customers');
        onClose();
        resetForm();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to create customer');
      }
    }
  );

  const resetForm = () => {
    setCustomerData({
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      companyName: '',
    });
    setAccountMode('none');
    setSelectedAccount(null);
    setNewAccountName('');
    setMakePrimaryBillingContact(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate email has a proper domain with TLD
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(customerData.email)) {
      toast.error('Please enter a valid email address (e.g., user@example.com)');
      return;
    }

    if (accountMode === 'link_existing' && !selectedAccount?.id) {
      toast.error('Select an account to link this customer');
      return;
    }
    if (accountMode === 'create_new' && !(newAccountName.trim() || customerData.companyName.trim())) {
      toast.error('Enter an account name');
      return;
    }

    createCustomerMutation.mutate({
      ...customerData,
      accountRelationship: {
        mode: accountMode,
        accountId: accountMode === 'link_existing' ? selectedAccount?.id || null : null,
        accountName: accountMode === 'create_new'
          ? newAccountName.trim() || customerData.companyName.trim()
          : null,
        makePrimaryBillingContact,
      },
    });
  };

  if (!isOpen) return null;

  return (
    <EntityDrawer
      open={isOpen}
      onClose={onClose}
      title="Create Customer"
      subtitle="Create a Stripe customer and optionally link them to an account."
      widthClassName="max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
          <div className="card p-4">
            <h4 className="text-md font-medium text-on-surface mb-2">Stripe Customer Details</h4>
            <p className="mb-4 text-sm text-on-surface-variant">
              This creates a customer directly in the connected Stripe account. No local ModalOS user login is created.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">First Name *</label>
                <input
                  type="text"
                  required
                  className="input-field"
                  value={customerData.firstName}
                  onChange={(e) => setCustomerData({ ...customerData, firstName: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Last Name *</label>
                <input
                  type="text"
                  required
                  className="input-field"
                  value={customerData.lastName}
                  onChange={(e) => setCustomerData({ ...customerData, lastName: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Email *</label>
                <input
                  type="email"
                  required
                  className="input-field"
                  value={customerData.email}
                  onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Phone *</label>
                <input
                  type="tel"
                  required
                  className="input-field"
                  value={customerData.phone}
                  onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Business / company label</label>
                <input
                  type="text"
                  className="input-field"
                  value={customerData.companyName}
                  onChange={(e) => setCustomerData({ ...customerData, companyName: e.target.value })}
                />
                <p className="mt-1 text-xs text-on-surface-variant">
                  This is display context unless you link or create an account below.
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <h4 className="text-md font-medium text-on-surface mb-2">Account relationship</h4>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {[
                { value: 'none', label: 'No account' },
                { value: 'link_existing', label: 'Link existing' },
                { value: 'create_new', label: 'Create account' },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`rounded-lg border p-3 text-sm ${
                    accountMode === option.value
                      ? 'border-primary bg-primary/10 text-on-surface'
                      : 'border-outline-variant text-on-surface-variant'
                  }`}
                >
                  <input
                    type="radio"
                    className="mr-2"
                    checked={accountMode === option.value}
                    onChange={() => setAccountMode(option.value as typeof accountMode)}
                  />
                  {option.label}
                </label>
              ))}
            </div>

            {accountMode === 'link_existing' ? (
              <div className="mt-4">
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">Account</label>
                <AccountTypeahead
                  value={selectedAccount}
                  onChange={setSelectedAccount}
                  placeholder="Search accounts..."
                />
              </div>
            ) : null}

            {accountMode === 'create_new' ? (
              <div className="mt-4">
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">Account name</label>
                <input
                  type="text"
                  className="input-field"
                  value={newAccountName}
                  onChange={(e) => setNewAccountName(e.target.value)}
                  placeholder={customerData.companyName || 'Business account name'}
                />
              </div>
            ) : null}

            {accountMode !== 'none' ? (
              <label className="mt-4 flex items-center gap-2 text-sm text-on-surface-variant">
                <input
                  type="checkbox"
                  checked={makePrimaryBillingContact}
                  onChange={(e) => setMakePrimaryBillingContact(e.target.checked)}
                />
                Make this customer the primary billing contact
              </label>
            ) : null}
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createCustomerMutation.isLoading}
              className="flex-1 btn-primary"
            >
              {createCustomerMutation.isLoading ? <LoadingSpinner size="sm" /> : 'Create Stripe Customer'}
            </button>
          </div>
      </form>
    </EntityDrawer>
  );
}

export function CustomerEditModal({ isOpen, onClose, customer }: CustomerEditModalProps) {
  const [customerData, setCustomerData] = useState<Partial<CustomerData>>({});
  const [syncToStripe, setSyncToStripe] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (customer) {
      setCustomerData({
        email: customer.email || '',
        firstName: customer.first_name || customer.firstName || '',
        lastName: customer.last_name || customer.lastName || '',
        phone: customer.phone || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        zipCode: customer.zip_code || customer.zipCode || '',
        dateOfBirth: customer.date_of_birth ? customer.date_of_birth.split('T')[0] : (customer.dateOfBirth || ''),
        licenseNumber: customer.license_number || customer.licenseNumber || '',
        licenseExpiry: customer.license_expiry ? customer.license_expiry.split('T')[0] : (customer.licenseExpiry || ''),
        emergencyContact: customer.emergency_contact || customer.emergencyContact || {
          name: '',
          phone: '',
          relationship: ''
        },
        companyName: customer.customer_company_name || customer.companyName || '',
        companyContact: customer.customer_company_contact || customer.companyContact || '',
        companyContactEmail: customer.customer_company_email || customer.companyContactEmail || '',
        companyContactPhone: customer.customer_company_phone || customer.companyContactPhone || '',
        scswCustomerId: customer.scsw_customer_id || customer.scswCustomerId || '',
        customerType: customer.customer_type || customer.customerType || '',
        wedding: customer.wedding ?? false,
        customerWebsite: customer.customer_website || customer.customerWebsite || '',
        customerStatus: customer.customer_status || customer.customerStatus || 'Active'
      });
    }
  }, [customer]);

  const updateCustomerMutation = useMutation(
    (data: Partial<CustomerData>) =>
      adminAPI.updateCustomer?.(customer?.id, { ...data, syncToStripe }) || Promise.resolve({ data: { success: true } }),
    {
      onSuccess: () => {
        toast.success('Customer updated successfully');
        queryClient.invalidateQueries('admin-customers');
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update customer');
      }
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (customerData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      if (!emailRegex.test(customerData.email)) {
        toast.error('Please enter a valid email address (e.g., user@example.com)');
        return;
      }
    }

    updateCustomerMutation.mutate(customerData);
  };

  if (!isOpen || !customer) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 backdrop-blur-sm">
      <div className="relative mx-auto my-4 max-h-[calc(100vh-2rem)] w-11/12 overflow-y-auto rounded-card bg-surface-container-lowest p-5 shadow-float md:my-8 md:w-3/4 lg:w-2/3">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-on-surface flex items-center space-x-2">
            <UserIcon className="h-6 w-6" />
            <span>Edit Customer</span>
          </h3>
          <button
            onClick={onClose}
            className="text-on-surface-variant hover:text-on-surface"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="card p-4">
            <h4 className="text-md font-medium text-on-surface mb-4">Basic Information</h4>
            <div className="mb-4 rounded-md border border-outline-variant bg-surface-container-low px-3 py-2">
              <label className="flex items-center gap-2 text-sm text-on-surface">
                <input
                  type="checkbox"
                  checked={syncToStripe}
                  onChange={(e) => setSyncToStripe(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>Sync name and profile changes to Stripe customer</span>
              </label>
              <p className="mt-1 text-xs text-on-surface-variant">
                Keep this enabled to update Stripe and local customer data together.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">First Name</label>
                <input
                  type="text"
                  className="input-field"
                  value={customerData.firstName || ''}
                  onChange={(e) => setCustomerData({ ...customerData, firstName: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Last Name</label>
                <input
                  type="text"
                  className="input-field"
                  value={customerData.lastName || ''}
                  onChange={(e) => setCustomerData({ ...customerData, lastName: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Email</label>
                <input
                  type="email"
                  className="input-field"
                  value={customerData.email || ''}
                  onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Phone</label>
                <input
                  type="tel"
                  className="input-field"
                  value={customerData.phone || ''}
                  onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Date of Birth</label>
                <input
                  type="date"
                  className="input-field"
                  value={customerData.dateOfBirth || ''}
                  onChange={(e) => setCustomerData({ ...customerData, dateOfBirth: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="card p-4">
            <h4 className="text-md font-medium text-on-surface mb-4">Address Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Street Address</label>
                <input
                  type="text"
                  className="input-field"
                  value={customerData.address || ''}
                  onChange={(e) => setCustomerData({ ...customerData, address: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">City</label>
                <input
                  type="text"
                  className="input-field"
                  value={customerData.city || ''}
                  onChange={(e) => setCustomerData({ ...customerData, city: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">State</label>
                <input
                  type="text"
                  className="input-field"
                  value={customerData.state || ''}
                  onChange={(e) => setCustomerData({ ...customerData, state: e.target.value })}
                  placeholder="CA, NY, TX..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">ZIP Code</label>
                <input
                  type="text"
                  className="input-field"
                  value={customerData.zipCode || ''}
                  onChange={(e) => setCustomerData({ ...customerData, zipCode: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Driver License Information */}
          <div className="card p-4">
            <h4 className="text-md font-medium text-on-surface mb-4">Driver License</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">License Number</label>
                <input
                  type="text"
                  className="input-field"
                  value={customerData.licenseNumber || ''}
                  onChange={(e) => setCustomerData({ ...customerData, licenseNumber: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">License Expiry</label>
                <input
                  type="date"
                  className="input-field"
                  value={customerData.licenseExpiry || ''}
                  onChange={(e) => setCustomerData({ ...customerData, licenseExpiry: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="card p-4">
            <h4 className="text-md font-medium text-on-surface mb-4">Emergency Contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Name</label>
                <input
                  type="text"
                  className="input-field"
                  value={customerData.emergencyContact?.name || ''}
                  onChange={(e) => setCustomerData({
                    ...customerData,
                    emergencyContact: { ...customerData.emergencyContact!, name: e.target.value }
                  })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Phone</label>
                <input
                  type="tel"
                  className="input-field"
                  value={customerData.emergencyContact?.phone || ''}
                  onChange={(e) => setCustomerData({
                    ...customerData,
                    emergencyContact: { ...customerData.emergencyContact!, phone: e.target.value }
                  })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Relationship</label>
                <input
                  type="text"
                  className="input-field"
                  value={customerData.emergencyContact?.relationship || ''}
                  onChange={(e) => setCustomerData({
                    ...customerData,
                    emergencyContact: { ...customerData.emergencyContact!, relationship: e.target.value }
                  })}
                  placeholder="Spouse, Parent, Friend..."
                />
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="card p-4">
            <h4 className="text-md font-medium text-on-surface mb-4">Business Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Company Name</label>
                <input
                  type="text"
                  className="input-field"
                  value={customerData.companyName || ''}
                  onChange={(e) => setCustomerData({ ...customerData, companyName: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Company Contact</label>
                <input
                  type="text"
                  className="input-field"
                  value={customerData.companyContact || ''}
                  onChange={(e) => setCustomerData({ ...customerData, companyContact: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Company Contact Email</label>
                <input
                  type="email"
                  className="input-field"
                  value={customerData.companyContactEmail || ''}
                  onChange={(e) => setCustomerData({ ...customerData, companyContactEmail: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Company Contact Phone</label>
                <input
                  type="tel"
                  className="input-field"
                  value={customerData.companyContactPhone || ''}
                  onChange={(e) => setCustomerData({ ...customerData, companyContactPhone: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">SCSW Customer ID</label>
                <input
                  type="text"
                  className="input-field"
                  value={customerData.scswCustomerId || ''}
                  onChange={(e) => setCustomerData({ ...customerData, scswCustomerId: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Customer Type</label>
                <select
                  className="input-field"
                  value={customerData.customerType || ''}
                  onChange={(e) => setCustomerData({ ...customerData, customerType: e.target.value })}
                >
                  <option value="">Select Type</option>
                  <option value="Corporate">Corporate</option>
                  <option value="Affiliate">Affiliate</option>
                  <option value="DMC/Event">DMC/Event</option>
                  <option value="Hotel">Hotel</option>
                  <option value="Association">Association</option>
                  <option value="School">School</option>
                  <option value="Retail">Retail</option>
                  <option value="Individual">Individual</option>
                  <option value="Wedding">Wedding</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Website</label>
                <input
                  type="url"
                  className="input-field"
                  value={customerData.customerWebsite || ''}
                  onChange={(e) => setCustomerData({ ...customerData, customerWebsite: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Status</label>
                <select
                  className="input-field"
                  value={customerData.customerStatus || 'Active'}
                  onChange={(e) => setCustomerData({ ...customerData, customerStatus: e.target.value })}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="flex items-center md:col-span-2">
                <input
                  type="checkbox"
                  id="editWedding"
                  checked={customerData.wedding ?? false}
                  onChange={(e) => setCustomerData({ ...customerData, wedding: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="editWedding" className="ml-2 text-sm text-on-surface">
                  Wedding
                </label>
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateCustomerMutation.isLoading}
              className="flex-1 btn-primary"
            >
              {updateCustomerMutation.isLoading ? <LoadingSpinner size="sm" /> : 'Update Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
